// @ts-nocheck
import {
  Box,
  Card as MantineCard,
  Group,
  SimpleGrid,
  Stack,
  Text as MantineText,
  Title,
  Code,
  useMantineColorScheme,
} from '@mantine/core';
import { IconMoon, IconSun } from '@tabler/icons-react';
import { Alert, Card, Text, ThemeSwitcher } from '../../../components/ui';
import { usePreferencesStore } from '../../../stores';

export default function ThemePage() {
  const { colorScheme } = useMantineColorScheme();
  const colorSchemeMode = usePreferencesStore((state) => state.colorSchemeMode);

  return (
    <Stack gap="xl">
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Theme Mode
        </Text>
        <MantineText c="dimmed" size="sm">
          Switch between light, dark, and auto theme modes. The theme automatically adapts all components.
        </MantineText>
      </Box>

      {/* Current Mode */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Box>
              <MantineText fw={600}>Current Theme Mode</MantineText>
              <MantineText size="sm" c="dimmed">
                Mode: {colorSchemeMode} | Active: {colorScheme}
              </MantineText>
            </Box>
            <Group gap="lg">
              {colorScheme === 'light' ? (
                <IconSun size={32} color="var(--mantine-color-yellow-6)" />
              ) : (
                <IconMoon size={32} color="var(--mantine-color-blue-6)" />
              )}
            </Group>
          </Group>
        </Stack>
      </MantineCard>

      {/* Theme Switcher Variants */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="lg">
          <Title order={4}>ThemeSwitcher Component</Title>

          <Stack gap="md">
            <Box>
              <MantineText size="sm" fw={500} mb="sm">
                Icon Button:
              </MantineText>
              <ThemeSwitcher variant="icon" size="lg" />
            </Box>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>
                Code:
              </MantineText>
              <Code block language="tsx">
{`<ThemeSwitcher variant="icon" size="lg" />`}
              </Code>
            </Stack>
          </Stack>

          <Stack gap="md">
            <Box>
              <MantineText size="sm" fw={500} mb="sm">
                Switch Toggle:
              </MantineText>
              <ThemeSwitcher variant="switch" size="md" />
            </Box>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>
                Code:
              </MantineText>
              <Code block language="tsx">
{`<ThemeSwitcher variant="switch" size="md" />`}
              </Code>
            </Stack>
          </Stack>

          <Stack gap="md">
            <Box>
              <MantineText size="sm" fw={500} mb="sm">
                Segmented Control (with labels):
              </MantineText>
              <ThemeSwitcher variant="segmented" size="md" showLabel />
            </Box>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>
                Code:
              </MantineText>
              <Code block language="tsx">
{`<ThemeSwitcher variant="segmented" size="md" showLabel />`}
              </Code>
            </Stack>
          </Stack>

          <Stack gap="md">
            <Box>
              <MantineText size="sm" fw={500} mb="sm">
                Segmented Control (icons only):
              </MantineText>
              <ThemeSwitcher variant="segmented" size="sm" />
            </Box>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>
                Code:
              </MantineText>
              <Code block language="tsx">
{`<ThemeSwitcher variant="segmented" size="sm" />`}
              </Code>
            </Stack>
          </Stack>
        </Stack>
      </MantineCard>

      {/* Themed Cards Demo */}
      <Box>
        <Title order={4} mb="md">
          Theme-Aware Card Variants
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <Card variant="default" p="lg">
            <Stack gap="xs">
              <MantineText fw={600}>Default Card</MantineText>
              <MantineText size="sm" c="dimmed">
                Standard bordered card
              </MantineText>
            </Stack>
          </Card>

          <Card variant="elevated" p="lg">
            <Stack gap="xs">
              <MantineText fw={600}>Elevated Card</MantineText>
              <MantineText size="sm" c="dimmed">
                Card with shadow
              </MantineText>
            </Stack>
          </Card>

          <Card variant="outlined" p="lg">
            <Stack gap="xs">
              <MantineText fw={600}>Outlined Card</MantineText>
              <MantineText size="sm" c="dimmed">
                Transparent with border
              </MantineText>
            </Stack>
          </Card>

          <Card variant="subtle" p="lg">
            <Stack gap="xs">
              <MantineText fw={600}>Subtle Card</MantineText>
              <MantineText size="sm" c="dimmed">
                Subtle background
              </MantineText>
            </Stack>
          </Card>
        </SimpleGrid>

        <Stack gap="xs" mt="md">
          <MantineText size="sm" fw={500}>
            Code:
          </MantineText>
          <Code block language="tsx">
{`<Card variant="default" p="lg">...</Card>
<Card variant="elevated" p="lg">...</Card>
<Card variant="outlined" p="lg">...</Card>
<Card variant="subtle" p="lg">...</Card>`}
          </Code>
        </Stack>
      </Box>

      {/* Interactive Cards */}
      <Box>
        <Title order={4} mb="md">
          Interactive Theme Cards
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card variant="elevated" hoverEffect="lift" p="xl">
            <Stack gap="md" align="center">
              <IconSun size={48} color="var(--mantine-color-yellow-6)" />
              <MantineText fw={600} size="lg">
                Light Mode
              </MantineText>
              <MantineText size="sm" c="dimmed" ta="center">
                Bright and clean interface for well-lit environments
              </MantineText>
            </Stack>
          </Card>

          <Card variant="elevated" hoverEffect="lift" p="xl">
            <Stack gap="md" align="center">
              <IconMoon size={48} color="var(--mantine-color-blue-6)" />
              <MantineText fw={600} size="lg">
                Dark Mode
              </MantineText>
              <MantineText size="sm" c="dimmed" ta="center">
                Easy on the eyes for low-light environments
              </MantineText>
            </Stack>
          </Card>
        </SimpleGrid>
      </Box>

      {/* Themed Alerts */}
      <Box>
        <Title order={4} mb="md">
          Theme-Aware Alerts
        </Title>
        <Stack gap="md">
          <Alert alertVariant="info" title="Information">
            This is an informational alert that adapts to the current theme.
          </Alert>
          <Alert alertVariant="success" title="Success">
            Operation completed successfully. The changes have been saved.
          </Alert>
          <Alert alertVariant="warning" title="Warning">
            Please review the changes before proceeding further.
          </Alert>
          <Alert alertVariant="danger" title="Error">
            An error occurred while processing your request.
          </Alert>
        </Stack>

        <Stack gap="xs" mt="md">
          <MantineText size="sm" fw={500}>
            Code:
          </MantineText>
          <Code block language="tsx">
{`<Alert alertVariant="info" title="Information">
  This is an informational alert...
</Alert>
<Alert alertVariant="success" title="Success">
  Operation completed successfully...
</Alert>`}
          </Code>
        </Stack>
      </Box>

      {/* Filled Alerts */}
      <Box>
        <Title order={4} mb="md">
          Filled Alert Variants
        </Title>
        <Stack gap="md">
          <Alert alertVariant="info" title="Info" filled>
            This is a filled information alert.
          </Alert>
          <Alert alertVariant="success" title="Success" filled>
            This is a filled success alert.
          </Alert>
          <Alert alertVariant="warning" title="Warning" filled>
            This is a filled warning alert.
          </Alert>
          <Alert alertVariant="danger" title="Error" filled>
            This is a filled error alert.
          </Alert>
        </Stack>

        <Stack gap="xs" mt="md">
          <MantineText size="sm" fw={500}>
            Code:
          </MantineText>
          <Code block language="tsx">
{`<Alert alertVariant="info" title="Info" filled>
  This is a filled information alert.
</Alert>`}
          </Code>
        </Stack>
      </Box>
    </Stack>
  );
}
